import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { IstatusItem, IstatusSet } from '@typings/status/statusItem.interface';

/**
 * 포토카드 스탯의 "장비 보정 제외" 최대값을 계산한다.
 *
 * 폴백 체인:
 *   1) photocardData[statusId] (양수일 때 사용)
 *   2) statusList에서 key 또는 id가 일치하는 항목의 defaultMaxValue
 *   3) statusSetting.max (전역 기본 최대값)
 *   4) 0
 *
 * - HP/MP 회복 아이템의 상한 clamp 계산에서도 본 함수를 사용해
 *   포토카드 문서에 최대값이 미설정된 경우에도 statusList / statusSetting으로
 *   폴백되도록 보장한다.
 * - StatusSec의 maxHpBase/maxMpBase 계산과 동일한 체인을 공용화하기 위한 진입점.
 *
 * @returns 최대값 (>= 0). 결정할 수 없으면 0을 반환한다.
 */
export function resolveMaxStat(params: {
  photocardData: Partial<IPhotoCard> | Record<string, unknown> | null | undefined;
  statusId: string;
  statusList?: IstatusItem[] | null;
  statusSetting?: IstatusSet | null;
}): number {
  const { photocardData, statusId, statusList, statusSetting } = params;
  if (!statusId) return 0;

  const rawFromData = (photocardData as Record<string, unknown> | null | undefined)?.[
    statusId
  ];
  const fromData = Number(rawFromData);
  if (Number.isFinite(fromData) && fromData > 0) return fromData;

  const matched = (statusList || []).find(
    (item) => item.key === statusId || item.id === statusId,
  );
  const fromDefault = Number(matched?.defaultMaxValue);
  if (Number.isFinite(fromDefault) && fromDefault > 0) return fromDefault;

  const fromGlobal = Number(statusSetting?.max);
  if (Number.isFinite(fromGlobal) && fromGlobal > 0) return fromGlobal;

  return 0;
}

/**
 * 전투 스탯의 "현재값"(current{Stat})을 안전하게 읽는다.
 * - current 필드가 Finite number이면 그대로 사용
 * - 그렇지 않으면 base 스탯 필드를 폴백으로 사용
 *
 * handleUseItem의 `?? 0` nullish 폴백 버그(0을 null로 취급하지 않음)를 회피하기 위한 헬퍼.
 */
export function resolveCurrentStat(params: {
  photocardData: Partial<IPhotoCard> | Record<string, unknown> | null | undefined;
  statusId: string;
  currentField: string;
}): number {
  const { photocardData, statusId, currentField } = params;
  const src = (photocardData as Record<string, unknown> | null | undefined) || {};

  const currentRaw = src[currentField];
  const currentNum = Number(currentRaw);
  if (Number.isFinite(currentNum)) return currentNum;

  const baseRaw = src[statusId];
  const baseNum = Number(baseRaw);
  if (Number.isFinite(baseNum)) return baseNum;

  return 0;
}
